import type { Product } from "@/types";

import { mapVariantType } from "../utils/map-variant-type";
import {
    readNumber,
    readString,
    readStringArray,
} from "../utils/opensearch-source-readers";
import { calculatePrice, getSalePrice } from "../utils/treez-pricing";

function readPrimarySizeLabel(
    source: Record<string, unknown>,
    productData: Record<string, unknown>,
): string {
    const customSize = readStringArray(source, "customSize");
    if (customSize[0]) {
        return customSize[0];
    }

    const customSizeString = readString(source, "customSize");
    if (customSizeString) {
        return customSizeString;
    }

    return readString(productData, "size");
}

/**
 * Headless returns `name` with a size suffix to disambiguate SKUs
 * (e.g. "blood orange gummies - 100 milligrams"). Prefer indexed `name`.
 */
function formatOrderProductDisplayName(
    source: Record<string, unknown>,
    productData: Record<string, unknown>,
): string {
    const indexedName = readString(source, "name");
    if (indexedName) {
        return indexedName.toLowerCase();
    }

    const baseName = readString(source, "menuTitle", "title");
    const sizeLabel = readPrimarySizeLabel(source, productData);

    if (baseName && sizeLabel) {
        return `${baseName} - ${sizeLabel}`.toLowerCase();
    }

    return baseName.toLowerCase();
}

function mapOpenSearchSourceToProduct(
    source: Record<string, unknown>,
): Product | null {
    const id = readString(source, "id", "product_id", "sku");
    if (!id) {
        return null;
    }

    const brand = readString(source, "brand", "brand_name", "brandName");
    const category = readString(source, "category", "category_name", "type");
    const amount = readNumber(source, "amount", "weight");
    const unitOfMeasurement = readString(source, "unitOfMeasurement");
    const availableUnits = readNumber(source, "availableUnits", "qty");

    const productDataRaw = source.productData;
    const productData =
        productDataRaw &&
        typeof productDataRaw === "object" &&
        !Array.isArray(productDataRaw)
            ? (productDataRaw as Record<string, unknown>)
            : {};

    const displayName = formatOrderProductDisplayName(source, productData);
    const menuTitle = readString(source, "menuTitle", "title", "name");
    const productName = readString(source, "name", "menuTitle", "title");

    const pricingRaw = productData.pricing;
    const pricing =
        pricingRaw &&
        typeof pricingRaw === "object" &&
        !Array.isArray(pricingRaw)
            ? (pricingRaw as Record<string, unknown>)
            : {};

    const priceSell = String(pricing.priceSell ?? pricing.price_sell ?? 0);
    const discountedPrice = String(
        pricing.discountedPrice ?? pricing.discounted_price ?? "",
    );

    const listPrice = calculatePrice(priceSell);
    const salePrice = getSalePrice(discountedPrice, listPrice);

    const images = readStringArray(productData, "images");
    const imageUrl = images[0] ?? "";
    const barcodes = readStringArray(productData, "barcodes");

    const variantType = mapVariantType({ amount, unitOfMeasurement });

    return {
        id,
        name: displayName,
        brand_name: brand,
        category,
        in_stock: availableUnits > 0,
        sales_price: salePrice,
        asset: imageUrl
            ? {
                  image: imageUrl,
                  thumbnail: imageUrl,
              }
            : undefined,
        barcodes: barcodes.length ? barcodes : undefined,
        variants: [
            {
                id,
                name: menuTitle || productName,
                pos_id: id,
                price: listPrice,
                salePrice,
                type: variantType,
            },
        ],
    };
}

export { mapOpenSearchSourceToProduct, formatOrderProductDisplayName };
